import React, { FC } from "react";
import { Box, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { HAMASPIK_CONTROL_HEIGHT } from "../../../../../assets/hamaspik/theme";
import Badge from "../../../../../assets/hamaspik/Badge";
import { PifFlow, PifFormValues } from "../../types";
import { pifFieldId } from "../../pifFocus";
import {
  pifEmailFormatMessage,
  pifLicenseFormatMessage,
  pifNameFormatMessage,
  pifPhoneFormatMessage,
  pifStateFormatMessage,
  pifZipFormatMessage,
} from "../../pifValidation";
import PifField, { PifFieldLabel } from "./PifField";
import PifSectionCard from "./PifSectionCard";

interface PifSectionContactProps {
  flow: PifFlow;
  emailRequired: boolean;
  /** From position-rules — the licence questions only apply where driving is allowed. */
  mayOperateVehicle: boolean;
  values: PifFormValues;
  onChange: (patch: Partial<PifFormValues>) => void;
}

const CONTACT_NOTE: Record<PifFlow, string> = {
  default: "",
  new: "At least one phone number is required.",
  active: "Read-only - the employee maintains these in self-service.",
  inactive: "Editable again - people move between jobs. Worth confirming.",
};

const PifSectionContact: FC<PifSectionContactProps> = ({ flow, emailRequired, mayOperateVehicle, values: f, onChange }) => {
  const lockContact = flow === "active";
  const phoneMissing = !f.home && !f.cell && flow !== "active";
  const showLicense = f.hasLicense === "yes";
  const licenseError = pifLicenseFormatMessage(f.licNum);
  const cityError = lockContact ? undefined : pifNameFormatMessage(f.city) ?? undefined;
  const stateError = lockContact ? undefined : pifStateFormatMessage(f.state) ?? undefined;
  const zipError = lockContact ? undefined : pifZipFormatMessage(f.zip) ?? undefined;
  const homeError = lockContact ? undefined : pifPhoneFormatMessage(f.home) ?? undefined;
  const cellError = lockContact ? undefined : pifPhoneFormatMessage(f.cell) ?? undefined;
  const emailError = lockContact ? undefined : pifEmailFormatMessage(f.email) ?? undefined;

  return (
    <PifSectionCard
      id="sec-contact"
      title="Contact & address"
      note={CONTACT_NOTE[flow]}
      badge={lockContact ? <Badge size="sm">LOCKED</Badge> : undefined}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.75 }}>
        {/* The ids are what a validation error focuses — see pifFocus / getPifGaps. */}
        <PifField label="Street address *">
          <TextField id={pifFieldId("addr1")} size="small" fullWidth value={f.addr1} disabled={lockContact} onChange={(e) => onChange({ addr1: e.target.value })} />
        </PifField>
        <PifField label="City *" error={cityError}>
          <TextField id={pifFieldId("city")} size="small" fullWidth value={f.city} disabled={lockContact} error={!!cityError} onChange={(e) => onChange({ city: e.target.value })} />
        </PifField>
        <PifField label="State *" error={stateError}>
          <TextField id={pifFieldId("state")} size="small" fullWidth value={f.state} disabled={lockContact} error={!!stateError} onChange={(e) => onChange({ state: e.target.value })} />
        </PifField>
        <PifField label="ZIP *" error={zipError}>
          <TextField id={pifFieldId("zip")} size="small" fullWidth value={f.zip} disabled={lockContact} error={!!zipError} onChange={(e) => onChange({ zip: e.target.value })} />
        </PifField>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.75 }}>
        {/* Home carries the id for the "at least one phone number" gap: either answers it, and this
            is the one the user reads first. */}
        <PifField label="Home phone" error={homeError}>
          <TextField id={pifFieldId("home")} size="small" fullWidth value={f.home} disabled={lockContact} error={!!homeError} placeholder="(718) 000-0000" onChange={(e) => onChange({ home: e.target.value })} />
        </PifField>
        <PifField label="Cell phone" error={cellError}>
          <TextField id={pifFieldId("cell")} size="small" fullWidth value={f.cell} disabled={lockContact} error={!!cellError} placeholder="(845) 000-0000" onChange={(e) => onChange({ cell: e.target.value })} />
          {phoneMissing && (
            <Typography variant="caption" color="error.main" data-testid="postif-phone-error">
              One phone number is required home or cell.
            </Typography>
          )}
        </PifField>
        <PifField label={`Personal email ${emailRequired && flow !== "active" ? "*" : ""}`} error={emailError}>
          <TextField id={pifFieldId("email")} size="small" fullWidth value={f.email} disabled={lockContact} error={!!emailError} placeholder="name@example.com" onChange={(e) => onChange({ email: e.target.value })} />
        </PifField>
      </Box>

      {mayOperateVehicle && (
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2.25, pt: 0.75, borderTop: "1px solid", borderColor: "divider", mt: 0.25 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1.75 }}>
          <PifFieldLabel label="Holds a driver's license? *" />
          <Box sx={{ display: "flex", gap: 1.25, "& .MuiButton-root": { minHeight: HAMASPIK_CONTROL_HEIGHT } }}>
            {/* Yes carries the id for the yes-or-no gap: a button pair has no single input to
                focus, and the first of the two is where a keyboard user expects to land. */}
            <Button id={pifFieldId("hasLicense")} variant={f.hasLicense === "yes" ? "contained" : "outlined"} size="small" onClick={() => onChange({ hasLicense: "yes" })}>
              Yes
            </Button>
            <Button
              variant={f.hasLicense === "no" ? "contained" : "outlined"}
              color={f.hasLicense === "no" ? "secondary" : "primary"}
              size="small"
              onClick={() => onChange({ hasLicense: "no", licNum: "" })}
            >
              No
            </Button>
          </Box>
        </Box>
        {showLicense && (
          <Box sx={{ display: "flex", gap: 1.75, flex: 1, minWidth: 340, pt: 1.75 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: 170 }}>
              <PifFieldLabel label="Issuing state" />
              <TextField size="small" fullWidth disabled value="New York" />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
              <PifFieldLabel label="License number *" />
              <TextField id={pifFieldId("licNum")} size="small" fullWidth value={f.licNum} error={!!licenseError} placeholder="9 digits, or 1 letter + 18 digits" onChange={(e) => onChange({ licNum: e.target.value })} />
              {licenseError && (
                <Typography variant="caption" color="error.main">
                  {licenseError}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
      )}
    </PifSectionCard>
  );
};

export default PifSectionContact;
